import {FC, createContext, useContext, useState, useEffect, useCallback, useRef} from 'react'
import {DefaultConfig} from './_LayoutConfig'
import {
  setLayoutIntoLocalStorage,
  getEmptyCssClasses,
  getEmptyCSSVariables,
  getEmptyHTMLAttributes,
  LayoutSetup,
} from './_LayoutSetup'
import {
  ILayout,
  ILayoutCSSVariables,
  ILayoutCSSClasses,
  ILayoutHTMLAttributes,
  LayoutType,
  ToolbarType,
} from './_Models'
import {API_URL, WithChildren} from '../../helpers'
import axios from 'axios'
import {
  API_CONNECTION_OFFLINE_EVENT,
  API_CONNECTION_ONLINE_EVENT,
} from '../../../app/modules/auth/core/AuthHelpers'

const SERVER_TIME_STORAGE_KEY = 'DateTimeShamsi'
const SERVER_TIME_REFRESH_MS = 10 * 60 * 1000

export interface LayoutContextModel {
  config: ILayout
  classes: ILayoutCSSClasses
  attributes: ILayoutHTMLAttributes
  cssVariables: ILayoutCSSVariables
  setLayout: (config: LayoutSetup) => void
  setLayoutType: (layoutType: LayoutType) => void
  setToolbarType: (toolbarType: ToolbarType) => void
  serverStatus: boolean
}

const LayoutContext = createContext<LayoutContextModel>({
  config: DefaultConfig,
  classes: getEmptyCssClasses(),
  attributes: getEmptyHTMLAttributes(),
  cssVariables: getEmptyCSSVariables(),
  setLayout: (config: LayoutSetup) => {},
  setLayoutType: (layoutType: LayoutType) => {},
  setToolbarType: (toolbarType: ToolbarType) => {},
  serverStatus: true,
})

const enableSplashScreen = () => {
  document.body.classList.add('page-loading')
  const splashScreen = document.getElementById('splash-screen')
  if (splashScreen) {
    splashScreen.style.setProperty('display', 'flex')
  }
}

const disableSplashScreen = () => {
  document.body.classList.remove('page-loading')
  const splashScreen = document.getElementById('splash-screen')
  if (splashScreen) {
    splashScreen.style.setProperty('display', 'none')
  }
}

const LayoutProvider: FC<WithChildren> = ({children}) => {
  const [config, setConfig] = useState(LayoutSetup.config)
  const [classes, setClasses] = useState(LayoutSetup.classes)
  const [attributes, setAttributes] = useState(LayoutSetup.attributes)
  const [cssVariables, setCSSVariables] = useState(LayoutSetup.cssVariables)
  const [serverStatus, setServerStatus] = useState(true)
  const lastServerTimeCheckAtRef = useRef(0)

  const checkNet = useCallback((force = false) => {
    if (!(process.env.REACT_APP_MEGAPAY_isLocal === 'true')) {
      const now = Date.now()
      if (!force && now - lastServerTimeCheckAtRef.current < SERVER_TIME_REFRESH_MS) {
        return
      }

      lastServerTimeCheckAtRef.current = now
      axios
        .get(`${API_URL}/ping/server`, {timeout: 10000})
        .then((data) => {
          setServerStatus(true)
          if (localStorage) {
            localStorage.setItem(SERVER_TIME_STORAGE_KEY, JSON.stringify(data.data))
          }
        })
        .catch(() => {
          lastServerTimeCheckAtRef.current = 0
          setServerStatus(false)
        })
    }
  }, [])

  useEffect(() => {
    if (process.env.REACT_APP_MEGAPAY_isLocal === 'true') {
      return
    }

    const handleBrowserOnline = () => {
      setServerStatus(true)
      checkNet(true)
    }
    const handleApiOnline = () => setServerStatus(true)
    const handleOffline = () => setServerStatus(false)
    const handleFocus = () => checkNet()
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkNet()
      }
    }

    checkNet(true)
    window.addEventListener('online', handleBrowserOnline)
    window.addEventListener('offline', handleOffline)
    window.addEventListener('focus', handleFocus)
    window.addEventListener(API_CONNECTION_ONLINE_EVENT, handleApiOnline)
    window.addEventListener(API_CONNECTION_OFFLINE_EVENT, handleOffline)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('online', handleBrowserOnline)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener(API_CONNECTION_ONLINE_EVENT, handleApiOnline)
      window.removeEventListener(API_CONNECTION_OFFLINE_EVENT, handleOffline)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [checkNet])

  const setLayout = (_themeConfig: Partial<ILayout>) => {
    enableSplashScreen()
    const bodyClasses = Array.from(document.body.classList)
    bodyClasses.forEach((cl) => document.body.classList.remove(cl))
    const updatedConfig = LayoutSetup.updatePartialConfig(_themeConfig)
    setConfig(Object.assign({}, updatedConfig))
    setClasses(LayoutSetup.classes)
    setAttributes(LayoutSetup.attributes)
    setCSSVariables(LayoutSetup.cssVariables)
    setTimeout(() => {
      disableSplashScreen()
    }, 500)
  }

  const setToolbarType = (toolbarType: ToolbarType) => {
    const updatedConfig = {...config}
    if (updatedConfig.app?.toolbar) {
      updatedConfig.app.toolbar.layout = toolbarType
    }

    setLayoutIntoLocalStorage(updatedConfig)
    window.location.reload()
  }

  const setLayoutType = (layoutType: LayoutType) => {
    const updatedLayout = {...config, layoutType}
    setLayoutIntoLocalStorage(updatedLayout)
    window.location.reload()
  }

  const value: LayoutContextModel = {
    config,
    classes,
    attributes,
    cssVariables,
    setLayout,
    setLayoutType,
    setToolbarType,
    serverStatus,
  }

  useEffect(() => {
    disableSplashScreen()
  }, [])

  return (
    <LayoutContext.Provider value={value}>
      {/* Temporarily disabled: offline connection alert */}
      {/* {!serverStatus && (
        <div className='w-100 py-5 px-10' style={{position: 'fixed', zIndex: '1000'}}>
          <div className='alert alert-danger w-100 text-center border-dotted shadow'>
            <i className='las la-broadcast-tower fs-3x text-danger me-5'></i>
            <div className='fw-bolder fs-5'>اتصال اینترنت خود را بررسی کنید</div>
          </div>
        </div>
      )} */}
      {children}
    </LayoutContext.Provider>
  )
}

export {LayoutContext, LayoutProvider}

export function useLayout() {
  return useContext(LayoutContext)
}
